/**
 * Per-chapter scene beats for planning / corkboard depth.
 * Preferred store: `chapter.scenes`. Falls back to `chapter.metadata.customFields.scenes`.
 */

import { chapterContentToPlainText } from "./chapterContent";
import type { Chapter } from "./models";

export type ChapterScene = {
  id: string;
  title: string;
  summary?: string;
  order: number;
};

export function generateChapterSceneId(): string {
  return `scene-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeScene(raw: unknown, index: number): ChapterScene | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const id =
    typeof record.id === "string" && record.id.trim()
      ? record.id.trim()
      : `scene-${index + 1}`;
  const title =
    typeof record.title === "string" && record.title.trim()
      ? record.title.trim()
      : `Scene ${index + 1}`;
  const order =
    typeof record.order === "number" && Number.isFinite(record.order)
      ? record.order
      : index + 1;
  const scene: ChapterScene = { id, title, order };
  if (typeof record.summary === "string") scene.summary = record.summary;
  return scene;
}

export function normalizeChapterScenes(value: unknown): ChapterScene[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(normalizeScene)
    .filter((s): s is ChapterScene => Boolean(s))
    .sort((a, b) => a.order - b.order)
    .map((scene, index) => ({ ...scene, order: index + 1 }));
}

function scenesFromCustomFields(chapter: Chapter): ChapterScene[] {
  const custom = chapter?.metadata?.customFields;
  if (!custom || typeof custom !== "object") return [];
  return normalizeChapterScenes((custom as Record<string, unknown>).scenes);
}

/** Read scenes from `chapter.scenes`, else customFields.scenes. */
export function getChapterScenes(chapter: Chapter): ChapterScene[] {
  if (Array.isArray(chapter.scenes) && chapter.scenes.length > 0) {
    return normalizeChapterScenes(chapter.scenes);
  }
  return scenesFromCustomFields(chapter);
}

/**
 * Ensure `chapter.scenes` is populated (migrating from customFields when needed).
 * Mutates chapter in place and returns the scenes list.
 */
export function ensureChapterScenes(chapter: Chapter): ChapterScene[] {
  const scenes = getChapterScenes(chapter);
  chapter.scenes = scenes;
  if (chapter.metadata?.customFields && "scenes" in chapter.metadata.customFields) {
    const nextFields = { ...chapter.metadata.customFields };
    delete nextFields.scenes;
    chapter.metadata = { ...chapter.metadata, customFields: nextFields };
  }
  return scenes;
}

/**
 * Heuristic split of chapter body into scenes.
 * Splits on lines that are only `***` / `* * *` / `---` or headings like `# Scene …`.
 */
export function splitScenesFromContent(content: unknown): ChapterScene[] {
  const plain = chapterContentToPlainText(content);
  if (!plain.trim()) return [];

  const lines = plain.split(/\r?\n/);
  const blocks: Array<{ title: string; body: string[] }> = [];
  let currentTitle = "Scene 1";
  let currentBody: string[] = [];
  let sceneIndex = 1;

  const isSeparator = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return false;
    if (/^\*{3,}$/.test(trimmed)) return true;
    if (/^\*\s+\*\s+\*$/.test(trimmed)) return true;
    if (/^-{3,}$/.test(trimmed)) return true;
    return false;
  };

  const isSceneHeading = (line: string) => {
    const trimmed = line.trim();
    const match = trimmed.match(/^#+\s*(Scene\b.*)$/i) || trimmed.match(/^Scene\s+\d+\b.*$/i);
    return match ? (match[1] ?? trimmed).trim() : null;
  };

  for (const line of lines) {
    if (isSeparator(line)) {
      if (currentBody.some((l) => l.trim()) || blocks.length === 0) {
        blocks.push({ title: currentTitle, body: currentBody });
      }
      sceneIndex += 1;
      currentTitle = `Scene ${sceneIndex}`;
      currentBody = [];
      continue;
    }
    const heading = isSceneHeading(line);
    if (heading) {
      if (currentBody.some((l) => l.trim()) || blocks.length > 0) {
        blocks.push({ title: currentTitle, body: currentBody });
        sceneIndex += 1;
      }
      currentTitle = heading;
      currentBody = [];
      continue;
    }
    currentBody.push(line);
  }

  if (currentBody.some((l) => l.trim()) || blocks.length === 0) {
    blocks.push({ title: currentTitle, body: currentBody });
  }

  // Single block with no markers → one scene titled from first line or "Scene 1".
  if (blocks.length === 1 && blocks[0]!.title === "Scene 1") {
    const bodyText = blocks[0]!.body.join("\n").trim();
    const firstLine = bodyText.split(/\n/).find((l) => l.trim())?.trim() ?? "";
    const title =
      firstLine.length > 0 && firstLine.length <= 80 ? firstLine.replace(/^#+\s*/, "") : "Scene 1";
    return [
      {
        id: generateChapterSceneId(),
        title,
        summary: bodyText.slice(0, 280) || undefined,
        order: 1,
      },
    ];
  }

  return blocks.map((block, index) => {
    const bodyText = block.body.join("\n").trim();
    return {
      id: generateChapterSceneId(),
      title: block.title,
      summary: bodyText.slice(0, 280) || undefined,
      order: index + 1,
    };
  });
}

/** Patch a scene by id; returns updated chapter (immutable). */
export function updateScene(
  chapter: Chapter,
  sceneId: string,
  patch: Partial<Pick<ChapterScene, "title" | "summary" | "order">>
): Chapter {
  const scenes = ensureChapterScenes(chapter).map((scene) => {
    if (scene.id !== sceneId) return scene;
    return {
      ...scene,
      ...(typeof patch.title === "string" ? { title: patch.title } : {}),
      ...(patch.summary !== undefined ? { summary: patch.summary } : {}),
      ...(typeof patch.order === "number" && Number.isFinite(patch.order)
        ? { order: patch.order }
        : {}),
    };
  });
  const ordered = [...scenes]
    .sort((a, b) => a.order - b.order)
    .map((scene, index) => ({ ...scene, order: index + 1 }));
  return { ...chapter, scenes: ordered };
}

/** Replace scenes on a chapter (e.g. after splitScenesFromContent). */
export function setChapterScenes(chapter: Chapter, scenes: ChapterScene[]): Chapter {
  const ordered = normalizeChapterScenes(scenes);
  return { ...chapter, scenes: ordered };
}
